import React, { useEffect, useRef } from 'react';
import { Button, useModalAttributes } from '@fluentui/react-components';
import { ArrowMinimize20Regular } from '@fluentui/react-icons';
import './GuideComponents.css';

/**
 * Vista ampliada de un bloque de la página (spec 008: grilla de consumos del proveedor "Expandir"). Derivado de la
 * guía (la guía no la dibuja): superficie de fondo de la app a pantalla completa, título 20 / Bold y botón
 * "Contraer" secundario arriba a la derecha. Es un diálogo modal (`role="dialog"`, `aria-modal`): Esc cierra, el
 * foco queda atrapado adentro (modalizer de tabster: Tab / Shift+Tab) y vuelve al control que la abrió al cerrar.
 *
 * Los hijos se montan siempre en el mismo lugar (abierta o no), así no pierden su estado al expandir / contraer.
 */

export interface GuideFullscreenProps {
  open: boolean;
  onClose: () => void;
  /** Título de la vista ampliada (nombre accesible del diálogo). */
  title: React.ReactNode;
  /** Texto del botón para salir ("Contraer"). */
  closeLabel: string;
  /** Contenido extra del encabezado (p. ej. una línea de totales compacta). */
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
  'data-testid'?: string;
}

export const GuideFullscreen = ({ open, onClose, title, closeLabel, headerExtra, children, ...rest }: GuideFullscreenProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useRef(`ichtys-fullscreen-${Math.random().toString(36).slice(2)}`).current;
  // Trampa de foco de Fluent (tabster): Tab / Shift+Tab no salen de la vista ampliada.
  const { modalAttributes } = useModalAttributes({ trapFocus: true, alwaysFocusable: true });

  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) previous.focus();
    };
  }, [open]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    if (event.key === 'Escape') {
      // Esc de un desplegable / diálogo interno lo cierra él (portales fuera de este nodo)
      if (event.defaultPrevented) return;
      event.stopPropagation();
      onClose();
      return;
    }
  };

  return (
    <div
      ref={ref}
      className={open ? 'ichtys-fullscreen' : 'ichtys-fullscreen-host'}
      role={open ? 'dialog' : undefined}
      aria-modal={open ? true : undefined}
      aria-labelledby={open ? titleId : undefined}
      {...(open ? modalAttributes : {})}
      onKeyDown={onKeyDown}
      data-testid={rest['data-testid']}
      data-open={open ? 'true' : 'false'}
    >
      {open && (
        <div className="ichtys-fullscreen__head">
          <h2 id={titleId} className="ichtys-fullscreen__title">
            {title}
          </h2>
          {headerExtra}
          <Button ref={closeRef} appearance="secondary" icon={<ArrowMinimize20Regular />} onClick={onClose} data-testid="ichtys-fullscreen-close">
            {closeLabel}
          </Button>
        </div>
      )}
      <div className={open ? 'ichtys-fullscreen__body' : 'ichtys-fullscreen-host__body'}>{children}</div>
    </div>
  );
};

export default GuideFullscreen;
